import { useState, useEffect, useCallback, useRef } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { 
  Card,
  CardBody,
  CardHeader,
  Chip,
  Divider,
  Skeleton
} from "@heroui/react";
import {
  Warning,
  Building,
  Shield,
  Receipt
} from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { InsurancePolicy } from "shared/types/insurance-policy";

interface Building {
  id: string;
  housenr: string;
  googlePlaceLongAddress?: string;
}

const InsuranceWarnings = () => {
  const [buildingsWithoutPolicies, setBuildingsWithoutPolicies] = useState<Building[]>([]);
  const [policiesWithoutPayments, setPoliciesWithoutPayments] = useState<InsurancePolicy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { session } = useSession();

  const hasLoadedRef = useRef(false);
  const fetchWarnings = useCallback(async () => {
    if (!session) return;
    
    try {
      setIsLoading(true);

      // Fetch buildings without policies
      const buildingsResponse = await fetchWithAuth(
        `${API_URL}/insurance/policies/buildings-without-policies`,
        { method: "GET" },
        session
      );

      if (buildingsResponse.ok) {
        const buildingsData = await buildingsResponse.json();
        setBuildingsWithoutPolicies(buildingsData);
      }

      // Fetch policies without payments
      const paymentsResponse = await fetchWithAuth(
        `${API_URL}/insurance/policies/policies-without-payments`,
        { method: "GET" },
        session
      );

      if (paymentsResponse.ok) {
        const paymentsData = await paymentsResponse.json();
        setPoliciesWithoutPayments(paymentsData);
      }

    } catch (error) {
      console.error("Failed to fetch insurance warnings:", error);
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      fetchWarnings();
    }
  }, [fetchWarnings]);

  const hasWarnings = buildingsWithoutPolicies.length > 0 || policiesWithoutPayments.length > 0;

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Skeleton className="h-40 rounded-lg" />
        <Skeleton className="h-40 rounded-lg" />
      </div>
    );
  }

  if (!hasWarnings) {
    return (
      <div className="bg-linear-to-r from-status-good/10 to-moss-primary/10 rounded-lg p-6 border border-status-good/20">
        <div className="flex items-center gap-3">
          <div className="shrink-0">
            <div className="w-10 h-10 rounded-full bg-status-good flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-status-good">Alles in orde!</h3>
            <p className="text-sm text-neutral-secondary-mid mt-1">
              Alle gebouwen zijn gedekt door verzekeringspolicies en alle policies hebben actuele premiebetalingen.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Buildings without policies */}
      {buildingsWithoutPolicies.length > 0 && (
        <Card className="border-2 border-red-200 bg-red-50">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3 w-full">
              <div className="shrink-0">
                <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                  <Warning className="w-5 h-5 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-700">Gebouwen zonder dekking</h3>
                <p className="text-sm text-red-600">
                  {buildingsWithoutPolicies.length} {buildingsWithoutPolicies.length === 1 ? 'gebouw heeft' : 'gebouwen hebben'} geen actieve verzekeringsdekking
                </p>
              </div>
            </div>
          </CardHeader>
          <Divider className="bg-red-200" />
          <CardBody className="pt-3">
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {buildingsWithoutPolicies.slice(0, 10).map((building) => (
                <div key={building.id} className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-red-500 shrink-0" />
                  <Chip 
                    size="sm" 
                    variant="flat" 
                    classNames={{
                      base: "bg-red-100 text-red-700",
                      content: "font-medium"
                    }}
                  >
                    {building.id}
                  </Chip>
                  {building.googlePlaceLongAddress && (
                    <span className="text-xs text-red-600 truncate">
                      {building.googlePlaceLongAddress}
                    </span>
                  )}
                </div>
              ))}
              {buildingsWithoutPolicies.length > 10 && (
                <div className="text-xs text-red-600 font-medium pt-2">
                  En nog {buildingsWithoutPolicies.length - 10} andere gebouwen...
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Policies without payments */}
      {policiesWithoutPayments.length > 0 && (
        <Card className="border-2 border-orange-200 bg-orange-50">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3 w-full">
              <div className="shrink-0">
                <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-orange-700">Ontbrekende premiebetalingen</h3>
                <p className="text-sm text-orange-600">
                  {policiesWithoutPayments.length} {policiesWithoutPayments.length === 1 ? 'polis heeft' : 'policies hebben'} geen premiebetaling voor dit jaar
                </p>
              </div>
            </div>
          </CardHeader>
          <Divider className="bg-orange-200" />
          <CardBody className="pt-3">
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {policiesWithoutPayments.slice(0, 10).map((policy) => (
                <div key={policy.id} className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-orange-500 shrink-0" />
                  <div className="flex items-center gap-2">
                    <Chip 
                      size="sm" 
                      variant="flat" 
                      classNames={{
                        base: "bg-orange-100 text-orange-700",
                        content: "font-medium"
                      }}
                    >
                      {policy.policyNumber}
                    </Chip>
                    <span className="text-sm text-orange-700 font-medium">
                      {policy.name}
                    </span>
                  </div>
                  {policy.yearlyPremium && (
                    <span className="text-xs text-orange-600 ml-auto">
                      €{policy.yearlyPremium.toLocaleString('nl-NL')}/jaar
                    </span>
                  )}
                </div>
              ))}
              {policiesWithoutPayments.length > 10 && (
                <div className="text-xs text-orange-600 font-medium pt-2">
                  En nog {policiesWithoutPayments.length - 10} andere policies...
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default InsuranceWarnings;
